import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const TableContext = createContext();

export const useTableContext = () => useContext(TableContext);

export function TableProvider({ children }) {
  const [tables, setTables] = useState({});

  const noPageRefreshRef = useRef(false);

  const [selectedAggregatorFilter, setSelectedAggregatorFilter] = useState('');
  const [selectedConsumerFilter, setSelectedConsumerFilter] = useState({
    consumerId: null,
    consumerFirstName: null,
    consumerLastName: null,
    message: null,
  });
  const [selectedFundingIdsFilter, setSelectedFundingIdsFilter] = useState({
    fundingIds: null,
    message: null,
  });
  const [selectedConsumerIdsFilter, setSelectedConsumerIdsFilter] = useState({
    consumerIds: null,
    message: null,
  });

  const getTable = useCallback(
    ({ tableName, initSortBy, initSortOrder, initRowsPerPage, initStatusFilter = '' }) => {
      if (tables[tableName]) {
        return tables[tableName];
      }

      return {
        sortBy: initSortBy,
        sortOrder: initSortOrder,
        page: 0,
        rowsPerPage: initRowsPerPage,
        totalRows: 0,
        rows: [],
        statusFilter: initStatusFilter,
        advanceEligibilityFilter: '',
        bankStatusFilter: '',
        flagsFilter: '',
        mineOnly: 'TRUE',
        search: '',
        modeFilter: '',
      };
    },
    [tables],
  );

  const updateTable = ({ tableName, newTable }) =>
    setTables(prevTables => ({
      ...prevTables,
      [tableName]: newTable,
    }));

  const sortHandler = useCallback(
    (tableName, field, invertSortOrder) => {
      const table = tables[tableName];

      setTables(prevTables => {
        const prevTable = prevTables[tableName];

        if (prevTable.sortBy === field) {
          return {
            ...prevTables,
            [tableName]: {
              ...prevTable,
              sortOrder: table.sortOrder === 'asc' ? 'desc' : 'asc',
            },
          };
        }

        return {
          ...prevTables,
          [tableName]: {
            ...prevTable,
            sortBy: field,
            sortOrder: invertSortOrder ? 'desc' : 'asc',
          },
        };
      });
    },
    [tables],
  );

  const handleChangePage = useCallback(
    (tableName, _, newPage) => {
      const table = tables[tableName];

      updateTable({
        tableName,
        newTable: {
          ...table,
          page: newPage,
        },
      });
    },
    [tables],
  );

  const handleChangeRowsPerPage = useCallback(
    (tableName, e) => {
      const table = tables[tableName];

      updateTable({
        tableName,
        newTable: {
          ...table,
          rowsPerPage: parseInt(e.target.value, 10),
          page: 0,
        },
      });
    },
    [tables],
  );

  const handleFilterUpdate = useCallback(
    (fieldName, tableName, value) => {
      const table = tables[tableName];

      updateTable({
        tableName,
        newTable: { ...table, [fieldName]: value, page: 0 },
      });
    },
    [tables],
  );

  const changeStatusFilter = useCallback(
    (tableName, value) => handleFilterUpdate('statusFilter', tableName, value),
    [handleFilterUpdate],
  );

  const changeAdvanceEligibilityFilter = useCallback(
    (tableName, value) => handleFilterUpdate('advanceEligibilityFilter', tableName, value),
    [handleFilterUpdate],
  );

  const changeBankStatusFilter = useCallback(
    (tableName, value) => handleFilterUpdate('bankStatusFilter', tableName, value),
    [handleFilterUpdate],
  );

  const changeFlagsFilter = useCallback(
    (tableName, value) => handleFilterUpdate('flagsFilter', tableName, value),
    [handleFilterUpdate],
  );

  const toggleMineOnly = useCallback(
    tableName => {
      const table = tables[tableName];

      updateTable({
        tableName,
        newTable: { ...table, mineOnly: table.mineOnly === 'TRUE' ? 'FALSE' : 'TRUE', page: 0 },
      });
    },
    [tables],
  );

  const searchKey = useCallback(
    (tableName, e) => {
      if (e.charCode === 13) {
        const table = tables[tableName];

        updateTable({
          tableName,
          newTable: { ...table, search: e.target.value, page: 0 },
        });
      }
    },
    [tables],
  );

  const changeModeFilter = useCallback(
    (tableName, e) => handleFilterUpdate('modeFilter', tableName, e.target.value),
    [handleFilterUpdate],
  );

  const clearTableFilters = useCallback(
    ({ tableName, initSortBy, initSortOrder, initRowsPerPage, initStatusFilter = '' }) => {
      const table = tables[tableName];

      updateTable({
        tableName,
        newTable: {
          ...table,
          sortBy: initSortBy,
          sortOrder: initSortOrder,
          page: 0,
          rowsPerPage: initRowsPerPage,
          totalRows: 0,
          rows: [],
          statusFilter: initStatusFilter,
          advanceEligibilityFilter: '',
          bankStatusFilter: '',
          flagsFilter: '',
          flaggedOnly: false,
          mineOnly: 'TRUE',
          search: '',
          modeFilter: '',
        },
      });

      setSelectedAggregatorFilter('');
      setSelectedConsumerFilter({
        consumerId: null,
        consumerFirstName: null,
        consumerLastName: null,
        message: null,
      });
    },
    [tables],
  );

  const value = useMemo(
    () => ({
      noPageRefreshRef,
      selectedAggregatorFilter,
      setSelectedAggregatorFilter,
      selectedConsumerFilter,
      setSelectedConsumerFilter,
      selectedFundingIdsFilter,
      setSelectedFundingIdsFilter,
      selectedConsumerIdsFilter,
      setSelectedConsumerIdsFilter,
      clearTableFilters,
      getTable,
      updateTable,
      sortHandler,
      handleChangePage,
      handleChangeRowsPerPage,
      changeStatusFilter,
      changeAdvanceEligibilityFilter,
      changeBankStatusFilter,
      changeFlagsFilter,
      toggleMineOnly,
      searchKey,
      changeModeFilter,
    }),
    [
      selectedAggregatorFilter,
      setSelectedAggregatorFilter,
      selectedConsumerFilter,
      setSelectedConsumerFilter,
      selectedFundingIdsFilter,
      setSelectedFundingIdsFilter,
      selectedConsumerIdsFilter,
      setSelectedConsumerIdsFilter,
      clearTableFilters,
      getTable,
      sortHandler,
      handleChangePage,
      handleChangeRowsPerPage,
      changeStatusFilter,
      changeAdvanceEligibilityFilter,
      changeBankStatusFilter,
      changeFlagsFilter,
      toggleMineOnly,
      searchKey,
      changeModeFilter,
    ],
  );

  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
}
